import { resourceIdZodSchema } from "@@/shared/acquire/resources/resource-id";
import { asDate } from "@@/shared/date-time/as-date";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { orderGroupIdZodSchema } from "@@/shared/order-groups/order-group-id";
import { orderIdZodSchema } from "@@/shared/orders/order-id";
import { pageIdZodSchema } from "@@/shared/pages/page-id";
import { payRequestIdZodSchema } from "@@/shared/pay-requests/pay-request-id";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type { SystemUser} from "@@/shared/users/system-user";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { asArray } from "@@/utilities/as-array";
import { z } from "zod";
import { accessKeyIdFactory, accessKeyIdZodSchema } from "./access-key-id";

const accessKeyZodSchema = z.object({
    _id: accessKeyIdZodSchema,
    _createdBy: userIdZodSchema.or(systemUserZodSchema),
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("ACCESS_KEY"),
    ownerId: providerIdZodSchema.optional(),
    accessTo: z.array(
        z.union([
            orderIdZodSchema,
            orderGroupIdZodSchema,
            pageIdZodSchema,
            payRequestIdZodSchema,
            resourceIdZodSchema,
        ]),
    ),
    expiresAt: z.date().optional().nullable(),
    permissions: z
        .record(
            z.union([orderIdZodSchema, orderGroupIdZodSchema, pageIdZodSchema]),
            z.array(z.string()),
        )
        .optional()
        .nullable(),
});

type AccessKey = z.infer<typeof accessKeyZodSchema>;

const accessKeyFactory = (params: {
    createdBy: UserId | SystemUser;
    expiresAt: Date | IsoAndUnixTimestamp | undefined;
    ownerId: ProviderId | undefined;
    accessTo: AccessKey["accessTo"][number] | AccessKey["accessTo"];
    permissions?: Record<AccessKey["accessTo"][number], string[]>;
}): AccessKey => {
    const expiresAt = asDate(params.expiresAt);
    const accessTo = asArray(params.accessTo);
    return {
        _id: accessKeyIdFactory(),
        _type: "ACCESS_KEY",
        _createdAt: nowIsoAndUnix(),
        _createdBy: params.createdBy,
        ownerId: params.ownerId,
        expiresAt,
        accessTo,
        permissions: params.permissions,
    };
};

export { accessKeyFactory, accessKeyZodSchema };
export type { AccessKey };
